import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { Pagination, useMediaQuery, useTheme } from "@mui/material";
import { loggedCloudRun } from "../../utils/apiLogger";
import Layout from "components/Layout";
import KPICard from "../../BranchesDashboard/components/KPICard";
import LineChart from "../../BranchesDashboard/components/LineChart";
import BarChart from "../../BranchesDashboard/components/BarChart";
import DonutChart from "../../BranchesDashboard/components/DonutChart";
import LoyaltyTierCard from "../../BranchesDashboard/components/LoyaltyTierCard";
import {
  KPISkeleton,
  ChartSkeleton,
  ListSkeleton,
} from "../../BranchesDashboard/components/DashboardSkeleton";
import { RootState } from "state";
import {
  setLoading,
  getCacheKey,
  isCacheValid,
  setCacheEntry,
} from "slices/dashboardFilters";
import { termsAndConditionsUrl, privacypolicyUrl } from "../../constants/urls";
import { getComparisonLabel } from "../../BranchesDashboard/utils/getComparisonLabel";
import styles from "./index.module.css";

interface CustomersData {
  totalUsers: {
    current: number;
    newThisMonth: number;
    change: number;
  };
  returningCustomers: {
    current: number;
    returningThisMonth: number;
    change: number;
  };
  oneTimeCustomers: number;
  avgPricePerCustomer: {
    current: number;
    previous: number;
    change: number;
  };
  customerGrowth: Array<{
    date: string;
    newCustomers: number;
    returningCustomers: number;
  }>;
  userRepeatFrequency: Array<{
    orders: string;
    count: number;
  }>;
  topSpenders: Array<{
    rank: number;
    name: string;
    phone: string;
    visits: number;
    revenue: number;
    lastVisit?: string;
  }>;
  loyaltyBreakdown: Array<{
    tier: "bronze" | "silver" | "gold" | "platinum";
    customers: number;
    revenue: number;
  }> | null;
  hasLoyaltyTiers?: boolean;
  orderTypes: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
}

const Customers: React.FC = () => {
  const {
    startDate,
    endDate,
    branchFilter: restaurantIds,
    loading,
    selectedDateOption,
  } = useSelector((state: RootState) => state.dashboardFilters);

  const [data, setData] = useState<CustomersData | null>(null);
  const [loadingState, setLoadingState] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isUsingCachedData, setIsUsingCachedData] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to force refresh
  const itemsPerPage = 5;

  // Separate loading states for progressive rendering
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingLoyalty, setLoadingLoyalty] = useState(true);
  const dataCache = useSelector(
    (state: RootState) => state.dashboardFilters.dataCache || {}
  );
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const toastShownRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  const forceRefreshRef = useRef(false);
  // Track which restaurantIds the current active request is for
  const currentRequestRestaurantIdsRef = useRef<string>("");
  // Track which restaurantIds the currently displayed data belongs to
  const dataRestaurantIdsRef = useRef<string>("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Helper functions for localStorage with 24h cache
  const getLocalStorageKey = (cacheKey: string) => {
    return `customers_data_${cacheKey}`;
  };

  const saveToLocalStorage = (
    cacheKey: string,
    data: CustomersData,
    timestamp?: number,
    restaurantIds?: string[]
  ) => {
    try {
      const storageKey = getLocalStorageKey(cacheKey);
      const now = timestamp || Date.now();
      const restaurantIdsKey = restaurantIds
        ? Array.from(restaurantIds).sort().join(",")
        : "";
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          data,
          timestamp: now,
          restaurantIds: restaurantIdsKey,
        })
      );
      console.log(
        "💾 [Customers] Saved to localStorage:",
        storageKey,
        "at",
        new Date(now),
        "restaurantIds:",
        restaurantIdsKey
      );
    } catch (error) {
      console.error("❌ [Customers] Failed to save to localStorage:", error);
    }
  };

  const loadFromLocalStorage = (
    cacheKey: string,
    currentRestaurantIds?: string[]
  ): { data: CustomersData; timestamp: number } | null => {
    try {
      const storageKey = getLocalStorageKey(cacheKey);
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
        const age = Date.now() - parsed.timestamp;

        if (age < CACHE_DURATION) {
          // Verify restaurantIds match if provided
          if (currentRestaurantIds && currentRestaurantIds.length > 0) {
            const currentIdsKey = Array.from(currentRestaurantIds)
              .sort()
              .join(",");
            const cachedIdsKey = parsed.restaurantIds || "";

            if (cachedIdsKey && currentIdsKey !== cachedIdsKey) {
              console.warn(
                "⚠️ [Customers] Cache restaurantIds mismatch - Expected:",
                currentIdsKey,
                "Got:",
                cachedIdsKey,
                "- Clearing wrong cache"
              );
              localStorage.removeItem(storageKey);
              return null;
            }
          }

          console.log(
            "📦 [Customers] Loaded valid cache from localStorage:",
            storageKey,
            "age:",
            Math.floor(age / 1000 / 60),
            "minutes",
            "restaurantIds:",
            parsed.restaurantIds || "unknown"
          );
          return { data: parsed.data, timestamp: parsed.timestamp };
        } else {
          console.log(
            "⏰ [Customers] Cache expired for:",
            storageKey,
            "age:",
            Math.floor(age / 1000 / 60 / 60),
            "hours"
          );
          // Remove expired cache
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.error("❌ [Customers] Failed to load from localStorage:", error);
    }
    return null;
  };

  const clearCurrentCacheOnly = (cacheKey: string) => {
    try {
      const storageKey = getLocalStorageKey(cacheKey);
      localStorage.removeItem(storageKey);
      console.log("🗑️ [Customers] Cleared cache for:", storageKey);
    } catch (error) {
      console.error("❌ [Customers] Failed to clear cache:", error);
    }
  };

  // Memoize restaurantIds to prevent unnecessary re-renders
  const restaurantIdsKey = useMemo(() => {
    const ids = restaurantIds || [];
    return Array.from(ids).sort().join(",");
  }, [restaurantIds]);

  // Helper function to check if loyalty cache is valid (24 hours)
  const isLoyaltyCacheValid = (entry: any): boolean => {
    if (!entry) return false;
    const LOYALTY_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
    return Date.now() - entry.timestamp < LOYALTY_CACHE_DURATION;
  };

  // Track if initial load from localStorage was done
  const initialLoadDoneRef = useRef(false);

  // Load from localStorage on mount only (not on every data change)
  useEffect(() => {
    // Only run once per filter change, not when data updates
    if (initialLoadDoneRef.current) return;

    if (restaurantIds && restaurantIds.length > 0 && startDate && endDate) {
      const cacheKey = getCacheKey(
        "getDashboardCustomers",
        restaurantIds || [],
        startDate,
        endDate
      );
      const cachedResult = loadFromLocalStorage(cacheKey, restaurantIds);
      const isOnline = navigator.onLine;

      // Load cached data if offline or if valid cache exists
      if (cachedResult) {
        console.log("📦 [Customers] Loading from localStorage");
        const currentIdsKey = Array.from(restaurantIds || [])
          .sort()
          .join(",");
        setData(cachedResult.data);
        // Track which restaurantIds this cached data belongs to
        dataRestaurantIdsRef.current = currentIdsKey;
        setLastUpdated(new Date(cachedResult.timestamp));

        if (!isOnline) {
          setIsUsingCachedData(true);
          setLoadingState(false);
          dispatch(setLoading(false));
          initialLoadDoneRef.current = true;
          if (!toastShownRef.current) {
            addToast("Showing cached data. You're offline.", {
              appearance: "info",
              autoDismiss: true,
            });
            toastShownRef.current = true;
          }
        } else {
          // Online with valid cache - show cached data but allow fetch in background
          setIsUsingCachedData(false);
          console.log("✅ [Customers] Using 24h cached data (still valid)");
        }
      }
    }
  }, [restaurantIds, startDate, endDate, addToast, dispatch]);

  // Reset flags and clear data when filters change to prevent showing wrong branch data
  useEffect(() => {
    initialLoadDoneRef.current = false;
    toastShownRef.current = false;
    fetchInProgressRef.current = false;
    // Clear request tracking when restaurantIds change
    currentRequestRestaurantIdsRef.current = "";
    // IMPORTANT: Clear data tracking ref when restaurantIds change
    dataRestaurantIdsRef.current = "";
    // IMPORTANT: Clear data immediately when restaurantIds change to prevent showing wrong branch data
    setData(null);
    setLoadingState(true);
    setLoadingMetrics(true);
    setLoadingCharts(true);
    setLoadingAnalytics(true);
    setLoadingLoyalty(true);
    console.log(
      "🔄 [Customers] Filters changed - cleared data, tracking refs, and request tracking"
    );
  }, [restaurantIdsKey, startDate, endDate]);

  // Store refs for offline handler to avoid re-attaching listeners
  const dataCacheRef = useRef(dataCache);
  const restaurantIdsRef = useRef(restaurantIds);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);

  // Update refs when values change
  useEffect(() => {
    dataCacheRef.current = dataCache;
    restaurantIdsRef.current = restaurantIds;
    startDateRef.current = startDate;
    endDateRef.current = endDate;
  }, [dataCache, restaurantIds, startDate, endDate]);

  // Listen for online/offline events to reload cached data when going offline
  useEffect(() => {
    const handleOnline = () => {
      console.log("🌐 [Customers] Back online");
      toastShownRef.current = false;
    };

    const handleOffline = () => {
      console.log("📴 [Customers] Went offline - loading cached data");
      const currentRestaurantIds = restaurantIdsRef.current;
      const currentStartDate = startDateRef.current;
      const currentEndDate = endDateRef.current;

      if (
        currentRestaurantIds &&
        currentRestaurantIds.length > 0 &&
        currentStartDate &&
        currentEndDate
      ) {
        const cacheKey = getCacheKey(
          "getDashboardCustomers",
          currentRestaurantIds || [],
          currentStartDate,
          currentEndDate
        );
        const localStorageData = loadFromLocalStorage(
          cacheKey,
          currentRestaurantIds
        );

        if (localStorageData) {
          console.log("✅ [Customers] Offline - Loading from localStorage");
          const currentIdsKey = Array.from(currentRestaurantIds || [])
            .sort()
            .join(",");
          setData(localStorageData.data);
          // Track which restaurantIds this cached data belongs to
          dataRestaurantIdsRef.current = currentIdsKey;
          setIsUsingCachedData(true);
          setLoadingState(false);
          dispatch(setLoading(false));
          if (!toastShownRef.current) {
            addToast("Showing cached data. Pull to refresh.", {
              appearance: "info",
              autoDismiss: true,
            });
            toastShownRef.current = true;
          }
        } else {
          // Try Redux cache as fallback
          const cached = (dataCacheRef.current || {})[cacheKey];
          if (cached && cached.data && cached.data.customers) {
            console.log("✅ [Customers] Offline - Loading from Redux cache");
            setData(cached.data.customers || cached.data);
            setIsUsingCachedData(true);
            setLoadingState(false);
            dispatch(setLoading(false));
            if (!toastShownRef.current) {
              addToast("Showing cached data. Pull to refresh.", {
                appearance: "info",
                autoDismiss: true,
              });
              toastShownRef.current = true;
            }
          }
        }
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [dispatch, addToast]); // Only re-attach when dispatch/addToast change (stable)

  useEffect(() => {
    const fetchData = async () => {
      if (
        !restaurantIds ||
        restaurantIds.length === 0 ||
        !startDate ||
        !endDate
      ) {
        console.warn(
          "⚠️ [Customers] No restaurants selected or date range missing"
        );
        setLoadingState(false);
        setData(null);
        return;
      }

      // Prevent duplicate fetches
      if (fetchInProgressRef.current) {
        console.log("⏳ [Customers] Fetch already in progress, skipping");
        return;
      }

      // Check cache first - using combined cache key for all customer data
      const cacheKey = getCacheKey(
        "getDashboardCustomers",
        restaurantIds || [],
        startDate,
        endDate
      );

      // Check localStorage for 24h cache first - ALWAYS check before any API calls
      // Skip cache check if forceRefresh is true (refresh button was clicked)
      const cachedResult = loadFromLocalStorage(cacheKey, restaurantIds);

      // If valid 24h cache exists and not forcing refresh, use it and skip ALL API calls
      if (cachedResult && !forceRefreshRef.current) {
        const currentIdsKey = Array.from(restaurantIds || [])
          .sort()
          .join(",");
        console.log(
          "✅ [Customers] Using valid 24h cached data - skipping all API calls",
          "restaurantIds verified:",
          currentIdsKey
        );
        setData(cachedResult.data);
        // Track which restaurantIds this cached data belongs to
        dataRestaurantIdsRef.current = currentIdsKey;
        setLastUpdated(new Date(cachedResult.timestamp));
        setIsUsingCachedData(false);
        setLoadingState(false);
        setLoadingMetrics(false);
        setLoadingCharts(false);
        setLoadingAnalytics(false);
        setLoadingLoyalty(false);
        dispatch(setLoading(false));
        fetchInProgressRef.current = false;
        return; // Exit early - no API calls needed
      }

      // Use ref to get current cache value without re-triggering effect
      const cached = (dataCacheRef.current || {})[cacheKey];

      // Try loading from localStorage if no valid 24h cache (for offline scenarios)
      const isOnline = navigator.onLine;

      // If offline, prioritize using any available cached data (even if expired)
      if (!isOnline) {
        // Fallback to Redux cache even if expired (better than nothing)
        if (cached && cached.data && cached.data.customers) {
          console.warn(
            "⚠️ [Customers] Offline - Using Redis cache as fallback"
          );
          setData(cached.data.customers || cached.data);
          setIsUsingCachedData(true);
          setLoadingState(false);
          dispatch(setLoading(false));
          if (!toastShownRef.current) {
            addToast("Showing cached data. You're offline.", {
              appearance: "info",
              autoDismiss: true,
            });
            toastShownRef.current = true;
          }
          return; // Skip API call when offline
        }

        // No cached data available at all
        console.warn("⚠️ [Customers] Offline and no cached data available");
        setError("No internet connection and no cached data available.");
        setLoadingState(false);
        dispatch(setLoading(false));
        return;
      }

      // Mark fetch as in progress
      fetchInProgressRef.current = true;

      // Capture current restaurantIds to verify responses match this request
      // This prevents race conditions when user switches branches quickly
      const requestRestaurantIdsKey = Array.from(restaurantIds)
        .sort()
        .join(",");
      currentRequestRestaurantIdsRef.current = requestRestaurantIdsKey;
      console.log(
        "🔒 [Customers] Locking request for restaurantIds:",
        requestRestaurantIdsKey
      );

      // Helper function to verify restaurantIds haven't changed
      const verifyRestaurantIdsMatch = (): boolean => {
        const currentIdsKey = Array.from(restaurantIds || [])
          .sort()
          .join(",");
        const matches =
          currentIdsKey === currentRequestRestaurantIdsRef.current;
        if (!matches) {
          console.warn(
            "⚠️ [Customers] RestaurantIds changed during request - ignoring response. Request:",
            currentRequestRestaurantIdsRef.current,
            "Current:",
            currentIdsKey
          );
        }
        return matches;
      };

      // Reset loading states
      setLoadingMetrics(true);
      setLoadingCharts(true);
      setLoadingAnalytics(true);
      setLoadingLoyalty(true);
      setLoadingState(true);
      setError(null);
      setIsUsingCachedData(false);

      // Clear old data immediately when fetching new restaurant
      if (
        dataRestaurantIdsRef.current &&
        dataRestaurantIdsRef.current !== requestRestaurantIdsKey
      ) {
        console.log(
          "🔄 [Customers] Clearing old data - restaurantIds changed from",
          dataRestaurantIdsRef.current,
          "to",
          requestRestaurantIdsKey
        );
        setData(null);
        dataRestaurantIdsRef.current = "";
      }

      // Initialize data with defaults so UI can render immediately
      setData({
        totalUsers: {
          current: 0,
          newThisMonth: 0,
          change: 0,
        },
        returningCustomers: {
          current: 0,
          returningThisMonth: 0,
          change: 0,
        },
        oneTimeCustomers: 0,
        avgPricePerCustomer: {
          current: 0,
          previous: 0,
          change: 0,
        },
        customerGrowth: [],
        userRepeatFrequency: [],
        topSpenders: [],
        orderTypes: [],
        loyaltyBreakdown: null,
        hasLoyaltyTiers: false,
      });

      console.log("🔍 [Customers] Starting data fetch...");

      try {
        // Convert moment timestamps to Date objects
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);

        // Ensure restaurantIds is a proper array
        const restaurantIdsArray = Array.isArray(restaurantIds)
          ? restaurantIds
          : [];

        const datePayload = {
          restaurantIds: restaurantIdsArray,
          startDate: startDateObj.toISOString(),
          endDate: endDateObj.toISOString(),
        };

        const loyaltyPayload = {
          restaurantIds: restaurantIdsArray,
        };

        console.log(
          "📤 [Customers] Calling new customer dashboard endpoints with:",
          { datePayload, loyaltyPayload }
        );
        console.log(
          "📋 [Customers] Restaurant IDs being sent:",
          restaurantIdsArray
        );

        // Fetch metrics first - display KPI cards as soon as ready
        loggedCloudRun("getDashboardCustomerMetrics", datePayload, {
          type: "main",
          context: "Customers-main-fetch",
        })
          .then((metricsResult) => {
            console.log("📥 [Customers] Metrics response:", metricsResult);
            setLoadingMetrics(false);

            // Verify restaurantIds haven't changed before processing response
            if (!verifyRestaurantIdsMatch()) {
              console.log(
                "⏭️ [Customers] Ignoring metrics response - restaurantIds changed"
              );
              return;
            }

            if (metricsResult?.success && metricsResult?.data) {
              setData((prevData) => {
                // Only merge with prevData if it belongs to the same restaurantIds
                const shouldUsePrevData =
                  prevData &&
                  dataRestaurantIdsRef.current === requestRestaurantIdsKey;
                const baseData: CustomersData = shouldUsePrevData
                  ? prevData
                  : {
                      totalUsers: {
                        current: 0,
                        newThisMonth: 0,
                        change: 0,
                      },
                      returningCustomers: {
                        current: 0,
                        returningThisMonth: 0,
                        change: 0,
                      },
                      oneTimeCustomers: 0,
                      avgPricePerCustomer: {
                        current: 0,
                        previous: 0,
                        change: 0,
                      },
                      customerGrowth: [],
                      userRepeatFrequency: [],
                      topSpenders: [],
                      orderTypes: [],
                      loyaltyBreakdown: null,
                      hasLoyaltyTiers: false,
                    };

                // Update tracking ref to indicate this data belongs to current restaurantIds
                dataRestaurantIdsRef.current = requestRestaurantIdsKey;

                return {
                  ...baseData,
                  totalUsers: {
                    current: metricsResult.data.totalUsers.current || 0,
                    newThisMonth:
                      metricsResult.data.totalUsers.newThisPeriod || 0,
                    change: metricsResult.data.totalUsers.change || 0,
                  },
                  returningCustomers: {
                    current: metricsResult.data.returningCustomers.current || 0,
                    returningThisMonth:
                      metricsResult.data.returningCustomers
                        .returningThisPeriod || 0,
                    change: metricsResult.data.returningCustomers.change || 0,
                  },
                  oneTimeCustomers: metricsResult.data.oneTimeCustomers || 0,
                  avgPricePerCustomer: {
                    current:
                      metricsResult.data.avgPricePerCustomer.current || 0,
                    previous:
                      metricsResult.data.avgPricePerCustomer.previous || 0,
                    change: metricsResult.data.avgPricePerCustomer.change || 0,
                  },
                };
              });

              // Set main loading state to false once metrics are loaded
              setLoadingState(false);
              dispatch(setLoading(false));
              setLastUpdated(new Date());
              fetchInProgressRef.current = false;
              console.log(
                "✅ [Customers] Metrics loaded - KPI Cards displayed"
              );
            }
          })
          .catch((err) => {
            console.error("❌ [Customers] Metrics error:", err);
            setLoadingMetrics(false);
            handleApiError(err, cacheKey, "metrics");
          });

        // Check loyalty cache first (24-hour cache) - before other API calls
        // Loyalty cache key only uses restaurantIds (no dates)
        const loyaltyCacheKey = `getLoyaltyTiers_${Array.from(
          restaurantIds || []
        )
          .sort()
          .join(",")}`;
        const cachedLoyalty = (dataCache || {})[loyaltyCacheKey];

        // If loyalty cache is valid (24 hours), use it immediately
        if (isLoyaltyCacheValid(cachedLoyalty)) {
          console.log("✅ [Customers] Using cached loyalty data (24h cache)");
          const cachedLoyaltyData = cachedLoyalty.data;
          if (cachedLoyaltyData) {
            const loyaltyBreakdown = cachedLoyaltyData.loyaltyBreakdown || null;
            const hasLoyaltyTiers = cachedLoyaltyData.hasLoyaltyTiers || false;

            setData((prevData) => {
              if (!prevData) {
                return {
                  totalUsers: {
                    current: 0,
                    newThisMonth: 0,
                    change: 0,
                  },
                  returningCustomers: {
                    current: 0,
                    returningThisMonth: 0,
                    change: 0,
                  },
                  oneTimeCustomers: 0,
                  avgPricePerCustomer: {
                    current: 0,
                    previous: 0,
                    change: 0,
                  },
                  customerGrowth: [],
                  userRepeatFrequency: [],
                  topSpenders: [],
                  orderTypes: [],
                  loyaltyBreakdown,
                  hasLoyaltyTiers,
                };
              }
              return {
                ...prevData,
                loyaltyBreakdown,
                hasLoyaltyTiers,
              };
            });

            setLoadingLoyalty(false);
            console.log("✅ [Customers] Loyalty loaded from cache");
          }
        } else {
          // Fetch loyalty tiers if not cached or cache expired
          loggedCloudRun("getLoyaltyTiers", loyaltyPayload, {
            type: "main",
            context: "Customers-main-fetch",
          })
            .then((loyaltyResult) => {
              console.log("📥 [Customers] Loyalty response:", loyaltyResult);
              setLoadingLoyalty(false);

              // Verify restaurantIds haven't changed before processing response
              if (!verifyRestaurantIdsMatch()) {
                console.log(
                  "⏭️ [Customers] Ignoring loyalty response - restaurantIds changed"
                );
                return;
              }

              if (loyaltyResult?.success && loyaltyResult?.data) {
                const loyaltyTiers = loyaltyResult.data.loyaltyTiers || [];
                const loyaltyBreakdown = loyaltyTiers
                  .map((tier: any) => {
                    const tierName = tier.tier?.toLowerCase() || "";
                    const validTiers: Array<
                      "bronze" | "silver" | "gold" | "platinum"
                    > = ["bronze", "silver", "gold", "platinum"];
                    if (!validTiers.includes(tierName as any)) {
                      return null;
                    }
                    return {
                      tier: tierName as
                        | "bronze"
                        | "silver"
                        | "gold"
                        | "platinum",
                      customers: tier.customers || 0,
                      revenue: tier.revenue || 0,
                    };
                  })
                  .filter((tier: any) => tier !== null);

                setData((prevData) => {
                  // Only merge with prevData if it belongs to the same restaurantIds
                  const shouldUsePrevData =
                    prevData &&
                    dataRestaurantIdsRef.current === requestRestaurantIdsKey;
                  if (!shouldUsePrevData) {
                    dataRestaurantIdsRef.current = requestRestaurantIdsKey;
                  }
                  if (!shouldUsePrevData) {
                    return {
                      totalUsers: {
                        current: 0,
                        newThisMonth: 0,
                        change: 0,
                      },
                      returningCustomers: {
                        current: 0,
                        returningThisMonth: 0,
                        change: 0,
                      },
                      oneTimeCustomers: 0,
                      avgPricePerCustomer: {
                        current: 0,
                        previous: 0,
                        change: 0,
                      },
                      customerGrowth: [],
                      userRepeatFrequency: [],
                      topSpenders: [],
                      orderTypes: [],
                      loyaltyBreakdown:
                        loyaltyBreakdown.length > 0 ? loyaltyBreakdown : null,
                      hasLoyaltyTiers:
                        loyaltyResult.data.hasLoyaltyTiers || false,
                    };
                  }
                  return {
                    ...prevData,
                    loyaltyBreakdown:
                      loyaltyBreakdown.length > 0 ? loyaltyBreakdown : null,
                    hasLoyaltyTiers:
                      loyaltyResult.data.hasLoyaltyTiers || false,
                  };
                });

                // Cache loyalty data separately with 24-hour expiration
                dispatch(
                  setCacheEntry({
                    key: loyaltyCacheKey,
                    data: {
                      loyaltyBreakdown:
                        loyaltyBreakdown.length > 0 ? loyaltyBreakdown : null,
                      hasLoyaltyTiers:
                        loyaltyResult.data.hasLoyaltyTiers || false,
                    },
                    branchIds: Array.from(restaurantIds || []),
                  })
                );

                console.log("✅ [Customers] Loyalty loaded and cached for 24h");
              }
            })
            .catch((err) => {
              console.error("❌ [Customers] Loyalty error:", err);
              setLoadingLoyalty(false);
              handleApiError(err, cacheKey, "loyalty");
            });
        }

        // Delay other requests slightly to prioritize metrics
        setTimeout(() => {
          // Fetch growth data - display charts as soon as ready
          loggedCloudRun("getDashboardCustomerGrowth", datePayload, {
            type: "main",
            context: "Customers-main-fetch",
          })
            .then((growthResult) => {
              console.log("📥 [Customers] Growth response:", growthResult);
              setLoadingCharts(false);

              // Verify restaurantIds haven't changed before processing response
              if (!verifyRestaurantIdsMatch()) {
                console.log(
                  "⏭️ [Customers] Ignoring growth response - restaurantIds changed"
                );
                return;
              }

              if (growthResult?.success && growthResult?.data) {
                setData((prevData) => {
                  // Only merge with prevData if it belongs to the same restaurantIds
                  const shouldUsePrevData =
                    prevData &&
                    dataRestaurantIdsRef.current === requestRestaurantIdsKey;
                  if (!shouldUsePrevData) {
                    dataRestaurantIdsRef.current = requestRestaurantIdsKey;
                  }
                  if (!shouldUsePrevData) {
                    return {
                      totalUsers: {
                        current: 0,
                        newThisMonth: 0,
                        change: 0,
                      },
                      returningCustomers: {
                        current: 0,
                        returningThisMonth: 0,
                        change: 0,
                      },
                      oneTimeCustomers: 0,
                      avgPricePerCustomer: {
                        current: 0,
                        previous: 0,
                        change: 0,
                      },
                      customerGrowth: growthResult.data.customerGrowth || [],
                      userRepeatFrequency: [],
                      topSpenders: [],
                      orderTypes: [],
                      loyaltyBreakdown: null,
                      hasLoyaltyTiers: false,
                    };
                  }
                  return {
                    ...prevData,
                    customerGrowth: growthResult.data.customerGrowth || [],
                  };
                });
                console.log("✅ [Customers] Growth loaded - Charts displayed");
              }
            })
            .catch((err) => {
              console.error("❌ [Customers] Growth error:", err);
              setLoadingCharts(false);
              handleApiError(err, cacheKey, "growth");
            });

          // Fetch analytics data - display tables and charts as soon as ready
          loggedCloudRun("getDashboardCustomerAnalytics", datePayload, {
            type: "main",
            context: "Customers-main-fetch",
          })
            .then((analyticsResult) => {
              console.log(
                "📥 [Customers] Analytics response:",
                analyticsResult
              );
              setLoadingAnalytics(false);

              // Verify restaurantIds haven't changed before processing response
              if (!verifyRestaurantIdsMatch()) {
                console.log(
                  "⏭️ [Customers] Ignoring analytics response - restaurantIds changed"
                );
                return;
              }

              if (analyticsResult?.success && analyticsResult?.data) {
                setData((prevData) => {
                  // Only merge with prevData if it belongs to the same restaurantIds
                  const shouldUsePrevData =
                    prevData &&
                    dataRestaurantIdsRef.current === requestRestaurantIdsKey;
                  if (!shouldUsePrevData) {
                    dataRestaurantIdsRef.current = requestRestaurantIdsKey;
                  }
                  if (!shouldUsePrevData) {
                    return {
                      totalUsers: {
                        current: 0,
                        newThisMonth: 0,
                        change: 0,
                      },
                      returningCustomers: {
                        current: 0,
                        returningThisMonth: 0,
                        change: 0,
                      },
                      oneTimeCustomers: 0,
                      avgPricePerCustomer: {
                        current: 0,
                        previous: 0,
                        change: 0,
                      },
                      customerGrowth: [],
                      userRepeatFrequency:
                        analyticsResult.data.userRepeatFrequency || [],
                      topSpenders: analyticsResult.data.topSpenders || [],
                      orderTypes: analyticsResult.data.orderTypes || [],
                      loyaltyBreakdown: null,
                      hasLoyaltyTiers: false,
                    };
                  }
                  return {
                    ...prevData,
                    userRepeatFrequency:
                      analyticsResult.data.userRepeatFrequency || [],
                    topSpenders: analyticsResult.data.topSpenders || [],
                    orderTypes: analyticsResult.data.orderTypes || [],
                  };
                });
                console.log(
                  "✅ [Customers] Analytics loaded - Tables displayed"
                );
              }
            })
            .catch((err) => {
              console.error("❌ [Customers] Analytics error:", err);
              setLoadingAnalytics(false);
              handleApiError(err, cacheKey, "analytics");
            });
        }, 100); // Small delay to prioritize metrics request

        // Helper function to handle API errors and show cached data
        const handleApiError = (
          error: any,
          cacheKey: string,
          endpoint: string
        ) => {
          const isNetworkError =
            error?.message?.includes("timeout") ||
            error?.message?.includes("network") ||
            error?.message?.includes("Failed to fetch") ||
            !navigator.onLine;

          // Try to use cached data from Redux first (use ref to avoid dependency issues)
          const cached = (dataCacheRef.current || {})[cacheKey];
          if (cached && cached.data && cached.data.customers) {
            console.warn(
              `⚠️ [Customers] Using Redux cached data after ${endpoint} error`
            );
            setData(cached.data.customers || cached.data);
            setIsUsingCachedData(true);
            setLoadingState(false);
            dispatch(setLoading(false));
            fetchInProgressRef.current = false;
            if (!toastShownRef.current) {
              addToast("Showing cached data. Pull to refresh.", {
                appearance: "info",
                autoDismiss: true,
              });
              toastShownRef.current = true;
            }
            return;
          }

          // Fallback to localStorage
          const localStorageData = loadFromLocalStorage(
            cacheKey,
            restaurantIds
          );
          if (localStorageData) {
            console.warn(
              `⚠️ [Customers] Using localStorage data after ${endpoint} error`
            );
            const currentIdsKey = Array.from(restaurantIds || [])
              .sort()
              .join(",");
            setData(localStorageData.data);
            // Track which restaurantIds this cached data belongs to
            dataRestaurantIdsRef.current = currentIdsKey;
            setIsUsingCachedData(true);
            setLoadingState(false);
            dispatch(setLoading(false));
            fetchInProgressRef.current = false;
            if (!toastShownRef.current) {
              addToast("Showing cached data. Pull to refresh.", {
                appearance: "info",
                autoDismiss: true,
              });
              toastShownRef.current = true;
            }
          } else {
            // Only show error if no cached data available
            console.error(
              `❌ [Customers] No cached data available after ${endpoint} error`
            );
            if (!data) {
              setError(
                isNetworkError
                  ? "No internet connection. Please check your network."
                  : "Unable to load data. Please check your connection."
              );
              setLoadingState(false);
              dispatch(setLoading(false));
              fetchInProgressRef.current = false;
            }
          }
        };
      } catch (error: any) {
        console.error("❌ [Customers] Exception caught:", error);
        setLoadingMetrics(false);
        setLoadingCharts(false);
        setLoadingAnalytics(false);
        setLoadingLoyalty(false);
        setLoadingState(false);
        dispatch(setLoading(false));
        fetchInProgressRef.current = false;
      }

      // Reset force refresh flag after fetch completes
      forceRefreshRef.current = false;
    };

    // Fetch data (will use cache if available, so safe to call on every change)
    fetchData();
  }, [
    restaurantIdsKey,
    startDate,
    endDate,
    dispatch,
    addToast,
    refreshTrigger,
  ]); // Removed dataCache - use ref instead

  // Ensure all 4 loyalty tiers are displayed, even if some are missing from API
  // This hook must be called before any early returns
  const allLoyaltyTiers = useMemo(() => {
    if (!data || !data.loyaltyBreakdown) {
      // Return all tiers with 0 values if no data
      return [
        { tier: "bronze" as const, customers: 0, revenue: 0 },
        { tier: "silver" as const, customers: 0, revenue: 0 },
        { tier: "gold" as const, customers: 0, revenue: 0 },
        { tier: "platinum" as const, customers: 0, revenue: 0 },
      ];
    }
    const tiers: Array<"bronze" | "silver" | "gold" | "platinum"> = [
      "bronze",
      "silver",
      "gold",
      "platinum",
    ];
    const breakdown = data.loyaltyBreakdown || [];

    return tiers.map((tier) => {
      const existingTier = breakdown.find((t) => t.tier === tier);
      return existingTier || { tier, customers: 0, revenue: 0 };
    });
  }, [data?.loyaltyBreakdown]);

  // Save data to localStorage whenever all data is loaded (24h cache)
  // Use current timestamp (Date.now()) to ensure fresh 24h expiration from fetch time
  // IMPORTANT: Only save if data belongs to currently selected restaurantIds
  useEffect(() => {
    if (
      data &&
      !loadingMetrics &&
      !loadingCharts &&
      !loadingAnalytics &&
      restaurantIds &&
      restaurantIds.length > 0 &&
      startDate &&
      endDate
    ) {
      // Verify that the data belongs to the currently selected restaurantIds
      const currentIdsKey = Array.from(restaurantIds).sort().join(",");

      // Only save if data belongs to current selection
      if (
        dataRestaurantIdsRef.current !== currentIdsKey &&
        dataRestaurantIdsRef.current !== ""
      ) {
        console.warn(
          "⚠️ [Customers] Skipping cache save - data belongs to different restaurantIds. Data:",
          dataRestaurantIdsRef.current,
          "Current:",
          currentIdsKey
        );
        return;
      }

      const cacheKey = getCacheKey(
        "getDashboardCustomers",
        restaurantIds,
        startDate,
        endDate
      );
      // Use current timestamp to ensure 24h cache from now
      const timestamp = Date.now();
      saveToLocalStorage(cacheKey, data, timestamp, restaurantIds);
      console.log(
        "💾 [Customers] Data saved to 24h cache with current timestamp",
        "restaurantIds:",
        currentIdsKey
      );
    }
  }, [
    data,
    loadingMetrics,
    loadingCharts,
    loadingAnalytics,
    restaurantIds,
    startDate,
    endDate,
  ]);

  // Show empty state if no restaurants selected
  if (!restaurantIds || restaurantIds.length === 0) {
    return (
      <Layout>
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>👥</div>
          <h3>Select a Branch to View Customer Analytics</h3>
          <p>
            Please select a branch from the dropdown above to view customer data
          </p>
        </div>
      </Layout>
    );
  }

  // Initialize data with defaults if not available yet
  const displayData: CustomersData = data || {
    totalUsers: {
      current: 0,
      newThisMonth: 0,
      change: 0,
    },
    returningCustomers: {
      current: 0,
      returningThisMonth: 0,
      change: 0,
    },
    oneTimeCustomers: 0,
    avgPricePerCustomer: {
      current: 0,
      previous: 0,
      change: 0,
    },
    customerGrowth: [],
    userRepeatFrequency: [],
    topSpenders: [],
    orderTypes: [],
    loyaltyBreakdown: null,
    hasLoyaltyTiers: false,
  };

  const totalPages = Math.ceil(
    (displayData.topSpenders || []).length / itemsPerPage
  );

  // Helper function to format the last updated time
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return diffInMinutes <= 1 ? "Just now" : `${diffInMinutes} Minutes ago`;
    } else if (diffInHours === 1) {
      return "1 Hour ago";
    } else if (diffInHours < 24) {
      return `${diffInHours} Hours ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? "1 Day ago" : `${diffInDays} Days ago`;
    }
  };

  const handleRefresh = () => {
    // Clear only the current date range cache to force refetch
    if (restaurantIds && restaurantIds.length > 0 && startDate && endDate) {
      const cacheKey = getCacheKey(
        "getDashboardCustomers",
        restaurantIds,
        startDate,
        endDate
      );
      clearCurrentCacheOnly(cacheKey);
      console.log(
        "🔄 [Customers] Refresh triggered - cleared current cache, will fetch live data"
      );
    }

    // Set force refresh flag to bypass cache checks
    forceRefreshRef.current = true;

    // Reset loading states and fetchInProgressRef to trigger new fetch
    fetchInProgressRef.current = false;
    setLoadingMetrics(true);
    setLoadingCharts(true);
    setLoadingAnalytics(true);
    setLoadingLoyalty(true);
    setLoadingState(true);

    // Trigger useEffect to run by incrementing refreshTrigger
    setRefreshTrigger((prev) => prev + 1);
  };
  const paginatedSpenders = (displayData.topSpenders || []).slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Layout>
      <div className={styles.container}>
        {isUsingCachedData && (
          <div className={styles.cachedDataBanner}>
            ℹ️ Showing cached data. Pull to refresh.
          </div>
        )}

        {/* Update Banner */}
        <div className={styles.updateBanner}>
          <span className={styles.updateText}>
            Updated: {getTimeAgo(lastUpdated)}
          </span>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={loadingMetrics}
            aria-label="Refresh data"
          >
            <img
              src="/refresh-ccw-02.svg"
              alt="refresh"
              className={styles.refreshIcon}
            />
          </button>
        </div>

        {/* KPI Cards Row - Display when metrics are loaded */}
        {loadingMetrics ? (
          <KPISkeleton />
        ) : (
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <KPICard
                title="Total Customers"
                metric={{
                  current: displayData.totalUsers.current || 0,
                  previous: displayData.totalUsers.newThisMonth || 0,
                  change: displayData.totalUsers.change || 0,
                  label: getComparisonLabel(selectedDateOption),
                }}
              />
            </div>
            <div className={styles.kpiCard}>
              <KPICard
                title="Returning Customers"
                metric={{
                  current: displayData.returningCustomers.current || 0,
                  previous:
                    displayData.returningCustomers.returningThisMonth || 0,
                  change: displayData.returningCustomers.change || 0,
                  label: getComparisonLabel(selectedDateOption),
                }}
                infoTooltip="Customers who have placed more than one order"
              />
            </div>
            <div className={styles.oneTimeCard}>
              <div className={styles.oneTimeLabelWrapper}>
                <div className={styles.oneTimeLabel}>Churned Customers</div>
                <div className={styles.oneTimeInfoWrapper}>
                  <img
                    src="/info-circle.svg"
                    alt="info"
                    className={styles.oneTimeInfoIcon}
                  />
                  <div className={styles.oneTimeTooltip}>
                    Customers who placed only one order and haven't returned
                  </div>
                </div>
              </div>
              <div className={styles.oneTimeValue}>
                {displayData.oneTimeCustomers || 0}
              </div>
              {isMobile && (
                <div className={styles.mobileInfoText}>
                  <img
                    src="/info-circle.svg"
                    alt="info"
                    className={styles.infoIcon}
                  />
                  Marketing Campaigns coming soon.
                </div>
              )}
              <div className={styles.retainButtonWrapper}>
                <button className={styles.retainButton} disabled>
                  <img
                    src="/message-dots-circle.svg"
                    alt="message dots"
                    className={styles.messageIcon}
                  />
                  Retain Customers
                </button>
                <div className={styles.tooltip}>
                  Marketing Campaigns coming soon.
                </div>
              </div>
            </div>
            <div className={styles.kpiCard}>
              <KPICard
                title="AVG Spend/Customer"
                metric={{
                  current: displayData.avgPricePerCustomer.current || 0,
                  previous: displayData.avgPricePerCustomer.previous || 0,
                  change: displayData.avgPricePerCustomer.change || 0,
                  label: getComparisonLabel(selectedDateOption),
                }}
                currency
              />
            </div>
          </div>
        )}

        {/* Charts Grid - Customer Growth and User Frequency - Display when data is loaded */}
        {loadingCharts ? (
          <ChartSkeleton />
        ) : (
          <div className={styles.chartsGrid}>
            <div className={styles.chartItem}>
              <LineChart
                key="customer-growth"
                title="Customer growth over time"
                data={
                  (displayData.customerGrowth || []).length > 0
                    ? (displayData.customerGrowth || []).map((d) => ({
                        date: d.date,
                        value: d.newCustomers,
                      }))
                    : [{ date: "No data", value: 0 }]
                }
                multiLine={
                  (displayData.customerGrowth || []).length > 0
                    ? {
                        labels: (displayData.customerGrowth || []).map(
                          (d) => d.date
                        ),
                        datasets: [
                          {
                            label: "New Customers",
                            data: (displayData.customerGrowth || []).map(
                              (d) => d.newCustomers || 0
                            ),
                            color: "#52B981",
                          },
                          {
                            label: "Returning Customers",
                            data: (displayData.customerGrowth || []).map(
                              (d) => d.returningCustomers || 0
                            ),
                            color: "#1E3A5F",
                          },
                        ],
                      }
                    : {
                        labels: ["No data"],
                        datasets: [
                          {
                            label: "New Customers",
                            data: [0],
                            color: "#52B981",
                          },
                          {
                            label: "Returning Customers",
                            data: [0],
                            color: "#1E3A5F",
                          },
                        ],
                      }
                }
              />
            </div>
            <div className={styles.chartItem}>
              <BarChart
                title="Customer Repeat Frequency"
                data={
                  (displayData.userRepeatFrequency || []).length > 0
                    ? (displayData.userRepeatFrequency || []).map((d) => ({
                        label: d.orders,
                        value: d.count || 0,
                      }))
                    : [{ label: "No data", value: 0 }]
                }
                horizontal
                color="#4A90E2"
                backgroundColor="rgba(1, 87, 125, 0.40)"
                hoverColor="#005073"
                showDecimals={false}
              />
            </div>
          </div>
        )}

        {/* Top Spenders Table - Display when analytics data is loaded */}
        {loadingAnalytics ? (
          <ListSkeleton />
        ) : (
          <div className={styles.topSpendersSection}>
            <div className={styles.sectionTitleWrapper}>
              <h3 className={styles.sectionTitle}>Top Spenders</h3>
              <div className={styles.topSpendersInfoWrapper}>
                <img
                  src="/info-circle.svg"
                  alt="info"
                  className={styles.topSpendersInfoIcon}
                />
                <div className={styles.topSpendersTooltip}>
                  Customers ranked by total revenue spent
                </div>
              </div>
            </div>
            <div className={styles.tableContainer}>
              <table className={styles.spendersTable}>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Visits</th>
                    <th>Last Visit</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSpenders.map((spender, index) => (
                    <tr key={spender.rank || index}>
                      <td className={styles.rankCell}>
                        <div className={styles.rankBadge}>{spender.rank}</div>
                      </td>
                      <td className={styles.nameCell}>
                        <div className={styles.nameWrapper}>
                          <div className={styles.name}>{spender.name}</div>
                          <div className={styles.phoneMobile}>
                            {spender.phone}
                          </div>
                        </div>
                      </td>
                      <td className={styles.phoneCell}>{spender.phone}</td>
                      <td className={styles.visitsCell}>
                        <div className={styles.visitsWrapper}>
                          <span className={styles.visitsNumber}>
                            {spender.visits}
                          </span>
                        </div>
                      </td>
                      <td className={styles.lastVisitCell}>
                        {spender.lastVisit || "-"}
                      </td>
                      <td className={styles.revenueCell}>
                        <div className={styles.revenueWrapper}>
                          <span className={styles.revenueNumber}>
                            {spender.revenue.toLocaleString("en-US")}
                          </span>
                          <span className={styles.revenueCurrency}> EGP</span>
                        </div>
                      </td>
                      <td className={styles.statsCell}>
                        <div className={styles.statsContainer}>
                          <div className={styles.visitsCellMobile}>
                            <div className={styles.visitsWrapper}>
                              <span className={styles.visitsNumber}>
                                {spender.visits}
                              </span>
                              <span className={styles.visitsLabel}>
                                {" "}
                                Visits
                              </span>
                            </div>
                          </div>
                          <div className={styles.revenueCellMobile}>
                            <div className={styles.revenueWrapper}>
                              <span className={styles.revenueNumber}>
                                {spender.revenue.toLocaleString("en-US")}
                              </span>
                              <span className={styles.revenueCurrency}>
                                {" "}
                                EGP
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={styles.lastVisitMobileCell}>
                        {spender.lastVisit && (
                          <div className={styles.lastVisitMobileWrapper}>
                            <svg
                              className={styles.calendarIcon}
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M11.375 2.33333H10.5V1.75C10.5 1.475 10.2812 1.25 10 1.25C9.71875 1.25 9.5 1.475 9.5 1.75V2.33333H4.5V1.75C4.5 1.475 4.28125 1.25 4 1.25C3.71875 1.25 3.5 1.475 3.5 1.75V2.33333H2.625C1.72813 2.33333 1 3.07812 1 4V11.6667C1 12.5635 1.72813 13.3333 2.625 13.3333H11.375C12.2719 13.3333 13 12.5635 13 11.6667V4C13 3.07812 12.2719 2.33333 11.375 2.33333ZM11.375 11.6667H2.625V6.25H11.375V11.6667Z"
                                fill="#9ca3af"
                              />
                            </svg>
                            <span>Last Visit: {spender.lastVisit}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && paginatedSpenders.length > 0 && (
                <div className={styles.pagination}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                    shape="rounded"
                    showFirstButton={false}
                    showLastButton={false}
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={isMobile ? 1 : 1}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        minWidth: isMobile ? "32px" : "36px",
                        height: isMobile ? "32px" : "36px",
                        margin: isMobile ? "0 2px" : "0 4px",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        background: "transparent",
                        color: "#000000",
                        fontSize: isMobile ? "12px" : "14px",
                        fontWeight: 400,
                        "&:hover": {
                          background: "rgba(0, 0, 0, 0.04)",
                        },
                        "&.Mui-selected": {
                          background: "rgba(1, 87, 125, 0.15)",
                          color: "#005073",
                          borderColor: "rgba(1, 87, 125, 0.3)",
                          fontWeight: 600,
                          "&:hover": {
                            background: "rgba(1, 87, 125, 0.25)",
                          },
                        },
                        "&.Mui-disabled": {
                          opacity: 0.4,
                        },
                      },
                      "& .MuiPaginationItem-ellipsis": {
                        color: "#000000",
                        border: "none",
                        background: "transparent",
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loyalty and Order Types - Display when data is loaded */}
        <div className={styles.bottomGrid}>
          <div className={styles.loyaltySection}>
            {loadingLoyalty ? (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      height: "24px",
                      width: "200px",
                      backgroundColor: "#f0f0f0",
                      borderRadius: "4px",
                      marginBottom: "16px",
                    }}
                  />
                </div>
                <ListSkeleton items={4} />
              </>
            ) : (
              <>
                <h3 className={styles.sectionTitle}>Loyalty Breakdown</h3>
                {/* Show placeholder only when loyalty program is explicitly disabled */}
                {displayData.hasLoyaltyTiers === false ? (
                  <div className={styles.loyaltyPlaceholder}>
                    <div className={styles.loyaltyPlaceholderContent}>
                      <img
                        src="/loyalty.png"
                        alt="Loyalty Program"
                        className={styles.loyaltyPlaceholderImage}
                      />
                      <p className={styles.loyaltyPlaceholderText}>
                        Join our{" "}
                        <span className={styles.loyaltyProgramSpan}>
                          loyalty program
                        </span>{" "}
                        to unlock this metric, contact us now
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className={styles.loyaltyList}>
                    {allLoyaltyTiers.map((tier) => (
                      <LoyaltyTierCard
                        key={tier.tier}
                        tier={tier.tier}
                        customers={tier.customers}
                        revenue={tier.revenue}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.orderTypesSection}>
            {loadingAnalytics ? (
              <ChartSkeleton />
            ) : (
              <DonutChart
                title="Order Type"
                data={
                  (displayData.orderTypes || []).length > 0
                    ? (displayData.orderTypes || []).map((ot) => ({
                        label: ot.type,
                        value: ot.count || 0,
                        percentage: ot.percentage || 0,
                      }))
                    : [{ label: "No data", value: 0, percentage: 0 }]
                }
                centerText="Total Orders"
                centerValue={
                  (displayData.orderTypes || []).length > 0
                    ? (displayData.orderTypes || []).reduce(
                        (acc, ot) => acc + (ot.count || 0),
                        0
                      )
                    : 0
                }
                colors={["#1E3A5F", "#52B981", "#F13C4F", "yellow"]}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerContent}>
            <span className={styles.copyright}>© Copyrights 2025 EZeats</span>
            <div className={styles.footerLinks}>
              <a
                href={termsAndConditionsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms & Conditions
              </a>
              <span>-</span>
              <a
                href={privacypolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Customers;
